import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { PageLoader } from "@/components/ui/skeleton-loaders";
import PageTransition from "@/components/PageTransition";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

// Lazy load non-critical routes to reduce initial bundle size
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Legal = lazy(() => import("./pages/Legal"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Support flow pages (dynamic per business)
const SupportEntry = lazy(() => import("./pages/support/SupportEntry"));
const SupportInstagram = lazy(() => import("./pages/support/SupportInstagram"));
const SupportReview = lazy(() => import("./pages/support/SupportReview"));
const SupportThankYou = lazy(() => import("./pages/support/SupportThankYou"));

// Cookie consent component
import CookieConsent from "@/components/CookieConsent";

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <PageTransition>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                    
                    {/* Support flow - dynamic per business */}
                    <Route path="/c/:slug" element={<SupportEntry />} />
                    <Route path="/c/:slug/instagram" element={<SupportInstagram />} />
                    <Route path="/c/:slug/review" element={<SupportReview />} />
                    <Route path="/c/:slug/merci" element={<SupportThankYou />} />
                    
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <CookieConsent />
                </PageTransition>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
